import { useCallback, useEffect, useState } from 'react'
import { formatCpf, digitsOnly } from '@/utils/cpf'
import { resolveCaptcha } from '@/utils/captcha'
import type { CaptchaConfig } from '@/components/AleloCaptcha'
import * as signupFlowMarker from '@/utils/signupFlowMarker'
import * as verificationApi from '@/utils/signupVerificationApi'
import * as verificationToken from '@/utils/signupVerificationToken'
import * as profileHandoff from '@/utils/signupProfileHandoff'
import * as signupDraft from '@/utils/signupDraft'
import { resolveSignupPhone } from '@/utils/signupPhone'
import * as loginCpfHint from '@/utils/loginCpfHint'
import { isUserExistsError } from '@/screens/signup-shared/userExists'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'
import type {
  StartResult,
  VerificationChannel,
} from '@/utils/signupVerificationApi'

type SignupPasswordSdk = {
  signup: (data: Record<string, string | number | boolean | undefined>) => Promise<void>
  validatePassword?: (password: string) => { valid: boolean; message?: string }
  getErrors?: () => TransactionErrorLike[]
  screen?: {
    loginLink?: string | null
    editLink?: string | null
    data?: {
      email?: string
      phoneNumber?: string
      username?: string
    } | null
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
}

async function createSdk(): Promise<SignupPasswordSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/signup-password')
    return new mod.default() as unknown as SignupPasswordSdk
  } catch {
    return null
  }
}

export function useSignupPasswordManager() {
  const [error, setError] = useState<string>()
  const [sdk, setSdk] = useState<SignupPasswordSdk | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk()
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const captchaConfig = resolveCaptcha(sdk?.screen ?? null)
  const captchaRequired = Boolean(captchaConfig?.provider)
  const onCaptchaToken = useCallback((t: string | undefined) => setCaptchaToken(t), [])

  // Identificadores vêm do passo `signup-id` via `screen.data`. No preview
  // local não há transação; `?cpf=&email=&phone=` substitui só ali. O celular
  // não é identificador na connection, então normalmente vem do handoff.
  const data = sdk?.screen?.data
  const preview = sdk ? null : new URLSearchParams(window.location.search)
  const username = data?.username || preview?.get('cpf') || ''
  const email = data?.email || preview?.get('email') || ''
  const phoneNumber = resolveSignupPhone({
    fromScreen: data?.phoneNumber,
    fromHandoff: profileHandoff.read(),
    fromPreview: preview?.get('phone'),
  })

  async function prepare(password: string) {
    setError(undefined)
    if (!password || password.length < 8) {
      throw new Error('PASSWORD_WEAK')
    }
    if (captchaRequired && !captchaToken) {
      throw new Error('CAPTCHA_REQUIRED')
    }

    const instance = sdk ?? (await createSdk())
    if (instance?.validatePassword) {
      const p = instance.validatePassword(password)
      if (p && p.valid === false) {
        setError('A senha não atende aos requisitos de segurança.')
        throw new Error('PASSWORD_POLICY')
      }
    }
    return instance
  }

  /**
   * Primeira metade: pede ao BFF o código do canal. Nenhuma conta é criada
   * aqui; sem canal exigido o BFF já devolve o token.
   *
   * Quando os steps do `signup-id` já confirmaram os canais, o token guardado
   * vale direto — o usuário não confirma o mesmo contato duas vezes. Token
   * ausente ou vencido cai no caminho normal de verificação desta tela.
   */
  async function startVerification(
    password: string,
    options: { channel?: VerificationChannel; sessionId?: string } = {},
  ): Promise<StartResult> {
    await prepare(password)

    const stored = verificationToken.take()
    if (stored) {
      return { ok: true, sessionId: options.sessionId || '', verified: true, token: stored }
    }

    return verificationApi.start({
      cpf: digitsOnly(username) || username,
      email,
      phone: phoneNumber,
      channel: options.channel,
      sessionId: options.sessionId,
    })
  }

  /**
   * Segunda metade: só aqui `instance.signup()` cria a conta, com o token de
   * verificação no payload — a Action `new-user-registration-validation`
   * nega o cadastro se ele faltar ou não conferir.
   */
  async function completeSignup(password: string, token: string) {
    const instance = await prepare(password)

    const payload: Record<string, string | undefined> = {
      // Campos de perfil digitados no step 1: sem eles a Action não grava
      // `user_metadata`, e a Auth0 preenche `name` com o e-mail.
      ...profileHandoff.read(),
      password,
      ...(email ? { email } : {}),
      ...(username ? { username: digitsOnly(username) || username } : {}),
      ...(phoneNumber ? { phoneNumber } : {}),
      'ulp-verification_token': token,
      ...(captchaToken ? { captcha: captchaToken } : {}),
    }

    if (instance?.signup) {
      signupFlowMarker.start()
      try {
        await instance.signup(payload)
      } catch (err) {
        signupFlowMarker.clear()
        throw err
      }
      return
    }

    signupFlowMarker.start()
    // O token nunca vai para o log (SBV-08); só o sinal de que foi incluído.
    console.info('[preview signup-password]', {
      ...payload,
      password: '***',
      'ulp-verification_token': '***',
    })
    window.dispatchEvent(
      new CustomEvent('alelo:acul-preview', {
        detail: { screen: 'signup-password', action: 'signup', data: payload },
      }),
    )
  }

  function handleEditIdentifier() {
    const link = sdk?.screen?.editLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=signup-id'
  }

  /** "Voltar" para o passo 3: o marcador faz o `signup-id` restaurar o wizard. */
  function handleBack() {
    signupDraft.markReturn()
    handleEditIdentifier()
  }

  function handleBackToLogin() {
    const link = sdk?.screen?.loginLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=login-id'
  }

  const displayId = username
    ? formatCpf(digitsOnly(username) || username)
    : email || phoneNumber

  // Falha do cadastro volta no re-render do Auth0; vira código (a tela
  // mostra a copy), nunca o texto do provedor.
  const serverError = toTransactionErrorCode(sdk?.getErrors?.())
  // A Auth0 recusou o `signup()` porque CPF, e-mail ou celular já têm conta
  // (store, Get User ou identificador único da connection). Ela não diz qual.
  const transactionErrors = sdk?.getErrors?.()
  const userExists = isUserExistsError(transactionErrors)
  if (transactionErrors?.length) {
    // Só os códigos, sem mensagem nem dado pessoal: identificam no UAT o
    // código do "user already exists" (spec signup-contact-in-use).
    console.info(
      '[signup-password] erros da transação',
      transactionErrors.map((e) => e.code),
    )
  }

  /** "Já tenho conta" com CPF existente: leva o CPF da tentativa para o `login-id`. */
  function goToLogin() {
    loginCpfHint.save(username || '')
    handleBackToLogin()
  }

  return {
    startVerification,
    completeSignup,
    handleEditIdentifier,
    handleBack,
    handleBackToLogin,
    goToLogin,
    userExists,
    displayId,
    email,
    /** Para onde cada código foi enviado, na copy da sub-etapa. */
    destinations: { email, sms: phoneNumber } as Record<VerificationChannel, string>,
    captchaConfig,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    error: error || serverError,
  }
}
